import React from 'react';
import { Card, Row, Col, Badge } from 'react-bootstrap';
import { useDrop } from 'react-dnd';
import { PIPELINE_STATUS_OPTIONS } from 'constants/globalConstants';
import { $filter, $view } from 'signals/global.signals';
import { handleClickStageHeader, handleDropItem } from 'components/global/_helpers/board.events';
import InvestorDealflowGridItem from 'components/views/InvestorDealflow/_components/InvestorDealflowGridItem';
import InvestorDealflowListItem from 'components/views/InvestorDealflow/_components/InvestorDealflowListItem';
import { updateDealflow } from 'api/dealflow.api';

const DropZone = ({ children, stageKey }) => {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: 'investor',
    canDrop: () => !$view.value.isDragDisabled,
    drop: async (item) => {
      if (item.dealflowItem.pipelineStatus !== stageKey) {
        await handleDropItem({
          droppedItem: item.dealflowItem,
          droppedStageValue: stageKey,
          updateCallback: updateDealflow,
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [stageKey, $view.value.isDragDisabled]);

  return (
    <div
      ref={$view.value.isDragDisabled ? null : drop}
      style={{
        backgroundColor: isOver && canDrop ? 'rgba(0, 255, 0, 0.1)' : 'transparent',
        border: isOver && canDrop ? '2px dashed #28a745' : 'none',
        borderRadius: '4px',
        minHeight: '100px',
        transition: 'all 0.2s ease',
      }}
      className={$view.value.isDragDisabled ? 'opacity-50' : 'cursor-move'}
    >
      {children}
    </div>
  );
};

const InvestorDealflowCards = ({ dealflowData }) => PIPELINE_STATUS_OPTIONS
  .filter((stage) => !$filter.value.pipelineStatuses?.length || $filter.value.pipelineStatuses.includes(stage.value))
  .map((stage, idx) => {
    const dealflowItems = dealflowData
      ?.filter((dealflowItem) => {
        const isInStage = stage.value.toLowerCase() === dealflowItem.pipelineStatus?.toLowerCase();
        const isStatusMatch = $filter.value.pipelineStatuses?.length
          ? $filter.value.pipelineStatuses?.includes(dealflowItem.pipelineStatus)
          : true;
        const isTeamMatch = $filter.value.teamId?.length > 0
          ? $filter.value.teamId?.includes(dealflowItem.teamId)
          : true;

        const isSearchMatch = $filter.value.pipelineSearch
          ? dealflowItem.companyName?.toLowerCase().includes($filter.value.pipelineSearch?.toLowerCase())
          : true;

        return isInStage && isStatusMatch && isTeamMatch && isSearchMatch;
      }) || [];

    return (
      <Card
        className={
          $view.value.view === 'grid'
            ? 'bg-transparent border-0 flex-shrink-0'
            : 'bg-transparent border-0 mb-16 w-100'
        }
        key={idx.toString()}
        style={
          $view.value.view === 'grid'
            ? {
              minWidth: 350,
              maxWidth: 400,
              width: 100,
              display: 'inline-block',
              verticalAlign: 'top',
            }
            : {
              width: '100%',
            }
        }
      >
        <Card.Header onClick={() => handleClickStageHeader(stage.id)}>
          <Row>
            <Col>
              <h6 className={`fw-600 mb-0 text-gradient-${stage.variant}-white`}>{stage.label}</h6>
            </Col>
            <Col xs="auto">
              <Badge bg="transparent" className="text-white">{dealflowItems?.length} in {stage.label}</Badge>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className={`bg-gray-900 px-40 ${$view.value.openStageCards?.includes(stage.id) ? 'd-block' : 'd-none'}`}>
          <DropZone stageKey={stage.value}>
            {dealflowItems?.map((dealflowItem, idxPipeline) => (
              <Row key={idxPipeline.toString()} className="mb-16">
                <Col
                  className=""
                  xs={12}
                >
                  {
                    $view.value.view === 'grid' ? (
                      <InvestorDealflowGridItem dealflowItem={dealflowItem} />
                    ) : (
                      <InvestorDealflowListItem dealflowItem={dealflowItem} />
                    )
                  }
                </Col>
              </Row>
            ))}
            {/* Todo: Add back in when we have a way to create dealflow items */}
            {/* <div className="d-flex justify-content-start align-items-center">
              <Button
                variant="transparent"
                size="sm"
                className="d-flex align-items-center text-gray-400"
                onClick={() => handleShowModal('investor-dealflow-create', { pipelineStatus: stage.value })}
              >
                <FontAwesomeIcon icon={faPlus} className="me-8" size="sm" />
                Add New
              </Button>
            </div> */}
          </DropZone>
        </Card.Body>
      </Card>
    );
  });

export default InvestorDealflowCards;
